import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { ROLE_LABELS } from "../lib/fcfa";
import {
  LayoutDashboard, Users, Shield, Coins, Settings, LogOut, Menu, X, Trophy,
  Briefcase, Receipt, ArrowRightLeft, CalendarDays, IdCard, Activity, FolderOpen,
  ShieldAlert, ClipboardList, Ticket,
} from "lucide-react";
import { useState } from "react";
import GlobalSearch from "./GlobalSearch";
import PoweredByBadge from "./PoweredByBadge";
import OfflineIndicator from "./OfflineIndicator";

const NAV = [
  { section: "Pilotage" },
  { to: "/app", icon: LayoutDashboard, label: "Tableau de bord", testid: "nav-dashboard", end: true },
  { to: "/app/calendar", icon: CalendarDays, label: "Calendrier", testid: "nav-calendar" },
  { section: "Effectif" },
  { to: "/app/teams", icon: Shield, label: "Équipes", testid: "nav-teams" },
  { to: "/app/players", icon: Users, label: "Joueurs", testid: "nav-players" },
  { to: "/app/performances", icon: Activity, label: "Performances", testid: "nav-performances" },
  { to: "/app/match-sheets", icon: ClipboardList, label: "Feuilles de match", testid: "nav-match-sheets" },
  { to: "/app/ticketing", icon: Ticket, label: "Billetterie", testid: "nav-ticketing" },
  { to: "/app/transfers", icon: ArrowRightLeft, label: "Transferts", testid: "nav-transfers" },
  { section: "Administration" },
  { to: "/app/employees", icon: Briefcase, label: "Employés", testid: "nav-employees" },
  { to: "/app/payslips", icon: Receipt, label: "Bulletins de paie", testid: "nav-payslips" },
  { to: "/app/finances", icon: Coins, label: "Finances", testid: "nav-finances" },
  { section: "Communauté" },
  { to: "/app/members", icon: IdCard, label: "Cartes membres", testid: "nav-members" },
  { to: "/app/documents", icon: FolderOpen, label: "Documents", testid: "nav-documents" },
  { section: "Configuration" },
  { to: "/app/settings", icon: Settings, label: "Paramètres", testid: "nav-settings" },
];

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = async () => { await logout(); navigate("/login"); };
  const exitImpersonation = async () => {
    if (!window.confirm("Quitter le mode support et revenir au panel admin ?")) return;
    await logout();
    navigate("/admin/panel");
  };
  if (!user) return null;
  const club = user.club || {};
  const primary = club.primary_color || "#9E1B1B";

  // Derive a darker shade for accents (decrease lightness ~15%)
  const darken = (hex, amount = 0.18) => {
    const c = hex.replace("#", "");
    const n = parseInt(c.length === 3 ? c.split("").map(x => x + x).join("") : c, 16);
    let r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
    r = Math.max(0, Math.round(r * (1 - amount)));
    g = Math.max(0, Math.round(g * (1 - amount)));
    b = Math.max(0, Math.round(b * (1 - amount)));
    return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
  };
  const primaryDark = darken(primary);
  const logoSrc = club.logo_url
    ? (club.logo_url.startsWith("http")
        ? club.logo_url
        : `${process.env.REACT_APP_BACKEND_URL}${club.logo_url}?auth=${localStorage.getItem("asc_token")}`)
    : null;

  return (
    <div className="min-h-screen flex bg-[#F4F5F7]" style={{ "--club-primary": primary, "--club-primary-dark": primaryDark }}>
      <aside
        className={`fixed inset-y-0 left-0 z-40 w-72 text-white flex flex-col transform transition-transform duration-200 ${
          open ? "translate-x-0" : "-translate-x-full"
        } lg:translate-x-0 lg:static lg:flex`}
        style={{ background: primaryDark }}
        data-testid="sidebar"
      >
        <div className="px-6 py-5 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-[#FBC02D] flex items-center justify-center overflow-hidden flex-shrink-0">
              {logoSrc ? (
                <img src={logoSrc} alt={club.name} className="w-full h-full object-cover" data-testid="sidebar-logo" />
              ) : (
                <Trophy className="w-5 h-5" style={{ color: primaryDark }} />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] tracking-overline text-white/70">Club</p>
              <p className="font-display font-bold text-base truncate" data-testid="sidebar-club-name">
                {club.name}
              </p>
            </div>
            <button className="lg:hidden text-white/80" onClick={() => setOpen(false)} data-testid="sidebar-close-btn">
              <X className="w-5 h-5" />
            </button>
          </div>
          <p className="text-xs text-white/60 mt-2">{club.city ? `${club.city}, ` : ""}{club.country}</p>
        </div>

        <nav className="flex-1 px-3 py-3 space-y-0.5 overflow-y-auto">
          {NAV.map((item, idx) => {
            if (item.section) {
              return (
                <p key={`s-${idx}`} className="px-4 text-[10px] tracking-overline text-white/45 mt-3 mb-1">
                  {item.section}
                </p>
              );
            }
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
                onClick={() => setOpen(false)}
                data-testid={item.testid}
              >
                <Icon className="w-4 h-4" />
                <span>{item.label}</span>
              </NavLink>
            );
          })}
        </nav>

        <div className="px-4 py-3 border-t border-white/10">
          <div className="px-2 py-1.5 mb-2">
            <p className="text-[10px] tracking-overline text-white/50">Connecté en tant que</p>
            <p className="text-sm font-medium truncate" data-testid="sidebar-user-label">{user.label || ROLE_LABELS[user.role]}</p>
            <p className="text-xs text-white/60">{ROLE_LABELS[user.role]}</p>
          </div>
          <button
            onClick={handleLogout}
            data-testid="logout-btn"
            className="w-full flex items-center gap-2 px-4 py-2 rounded-md text-white/85 hover:bg-white/10 transition text-sm"
          >
            <LogOut className="w-4 h-4" /> Déconnexion
          </button>
        </div>
      </aside>

      {open && (
        <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setOpen(false)} />
      )}

      <div className="flex-1 flex flex-col min-w-0">
        {user.is_impersonation && (
          <div className="bg-[#FCD116] text-[#121212] px-4 lg:px-8 py-2 flex items-center justify-between gap-3 border-b-2 border-[#E8BC0F]" data-testid="impersonation-banner">
            <div className="flex items-center gap-2 text-sm">
              <ShieldAlert className="w-4 h-4 flex-shrink-0" />
              <p className="font-semibold">
                Mode support WSDK — Vous agissez en tant que président de <strong>{club.name}</strong>. Toutes vos actions sont consignées.
              </p>
            </div>
            <button onClick={exitImpersonation} className="text-xs font-bold uppercase tracking-wider px-3 py-1.5 bg-[#121212] text-[#FCD116] rounded hover:bg-[#2A2A2A] transition flex-shrink-0" data-testid="exit-impersonation-btn">
              Quitter
            </button>
          </div>
        )}
        <header className="bg-white border-b border-[#E0E0E0] px-4 lg:px-8 h-16 flex items-center justify-between sticky top-0 z-20">
          <div className="flex items-center gap-3">
            <button onClick={() => setOpen(true)} className="lg:hidden text-[#121212]" data-testid="sidebar-open-btn">
              <Menu className="w-5 h-5" />
            </button>
            <p className="text-xs tracking-overline text-[#616161] hidden sm:block">WSDK Connect Manager</p>
          </div>
          <div className="flex items-center gap-3">
            <OfflineIndicator />
            <GlobalSearch />
            <div className="text-right hidden sm:block">
              <p className="text-xs text-[#616161]">{club.sport || "Football"}</p>
              <p className="text-sm font-semibold truncate max-w-[180px]">{club.name}</p>
            </div>
            <div className="w-9 h-9 rounded-full text-white flex items-center justify-center font-display font-bold text-sm overflow-hidden"
                 style={{ backgroundColor: primary }}>
              {logoSrc ? (
                <img src={logoSrc} alt="" className="w-full h-full object-cover" />
              ) : (
                (club.name || "C").slice(0, 2).toUpperCase()
              )}
            </div>
          </div>
        </header>

        <main className="flex-1 p-4 lg:p-8 overflow-x-hidden" data-testid="main-content">
          <Outlet />
        </main>
      </div>
      <PoweredByBadge />
    </div>
  );
}
